import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ConfirmSheet from '@/components/ConfirmSheet';
import { User, ArrowLeft, Phone, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * Mandatory name capture layer — shown immediately after OTP verification for any user
 * without a name on file. Cannot be skipped except by explicitly going back to the phone
 * entry screen (which signs the user out and returns them to /login).
 */
export default function WelcomeNamePage() {
  const { user, api, refreshUser, isAdminOnlySession, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [backing, setBacking] = useState(false);
  const [confirmBackOpen, setConfirmBackOpen] = useState(false);

  // Safety: if already has a name, or is an admin-only session, skip this page.
  useEffect(() => {
    if (!user) return;
    if (isAdminOnlySession && isAdminOnlySession()) {
      navigate('/admin', { replace: true });
      return;
    }
    if (user.name) navigate('/', { replace: true });
  }, [user, navigate, isAdminOnlySession]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error('Please enter your name (at least 2 characters)');
      return;
    }
    setSaving(true);
    try {
      await api.put('/profile', { name: trimmed });
      await refreshUser();
      toast.success(`Welcome, ${trimmed.split(' ')[0]}!`);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save your name');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    if (backing || saving) return;
    setBacking(true);
    try {
      await logout();
    } catch (_e) { /* proceed regardless */ }
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F6F8FB] flex flex-col" data-testid="welcome-name-page">
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col p-6">
        {/* Back button */}
        <button
          type="button"
          data-testid="welcome-back-btn"
          onClick={() => setConfirmBackOpen(true)}
          disabled={backing || saving}
          aria-label="Back to phone number"
          className="w-10 h-10 -ml-1 rounded-full bg-[#F6F8FB] border border-[#E4E8EF] flex items-center justify-center text-[#0B1620] hover:bg-[#EFF2F7] active:scale-95 transition-all disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="pt-8 pb-8 animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight leading-tight">
            Please add your name<span className="text-[#0B4F3F]">.</span>
          </h1>
          <p className="text-[15px] text-[#5B6675] mt-2 leading-relaxed">
            Farmers will see this when you reach out to them.
          </p>
          {user.phone && (
            <div className="mt-4 inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full bg-[#F6F8FB] border border-[#E4E8EF]" data-testid="welcome-phone-chip">
              <Phone className="w-3 h-3 text-[#0B4F3F]" />
              <span className="text-[12px] text-[#0B1620] font-medium tabular-nums tracking-wide">{user.phone}</span>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="animate-fade-in-up stagger-1 space-y-5">
          <div>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6675]" />
              <Input
                data-testid="welcome-name-input"
                type="text"
                autoFocus
                placeholder="e.g. Rajesh Patel"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                className="pl-11 h-14 text-base bg-[#F1F5FA] border-[#E4E8EF] rounded-2xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
              />
            </div>
            <p className="text-xs text-[#5B6675] mt-2">You can update this anytime from your profile.</p>
          </div>

          <Button
            data-testid="welcome-name-save-btn"
            type="submit"
            disabled={saving || name.trim().length < 2}
            className="w-full h-13 bg-[#0B4F3F] hover:bg-[#083D31] text-white rounded-full font-semibold text-base shadow-[0_10px_24px_-8px_rgba(11,79,63,0.5)] disabled:shadow-none"
          >
            {saving ? 'Saving…' : 'Continue to Frootex'}
          </Button>
        </form>

        <div className="flex-1" />

        <p className="text-[11px] text-[#5B6675]/70 text-center pb-6">
          Required · takes 5 seconds
        </p>
      </div>

      {/* Confirm sign-out sheet */}
      <ConfirmSheet
        open={confirmBackOpen}
        onOpenChange={setConfirmBackOpen}
        title="Sign out and go back?"
        description="Going back will sign you out of Frootex. You'll need to verify your phone number again to continue."
        confirmLabel="Sign out"
        cancelLabel="Stay here"
        destructive
        icon={<LogOut className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={handleBack}
        loading={backing}
        testId="welcome-back-confirm"
      />
    </div>
  );
}
